<template>
    <div class="wp-4 mx-auto upload-area">
        <el-form>
            <el-form-item>
                <el-upload
                    drag
                    ref="uploader"
                    multiple
                    :accept="mimeTypes"
                    :action="actionUploadUrl"
                    :fileList="fileList"
                    :onError="handleError"
                    :onSuccess="handleSuccess"
                    :onExceed="handleExceed"
                    :beforeUpload="handleBeforeUpload"
                    :showFileList="false"
                    :limit="50"
                    class="wp-10"
                    :disabled="technicalIssue"
                    :class="{
                        'undrag shadow':
                            isProcessing || isUploading || technicalIssue || errorFileList.length,
                    }"
                >
                    <div class="flex-column hp-10 jc-center ai-center" @click="handleClickUploader">
                        <template v-if="isProcessing || isUploading">
                            <div class="loader loader-arrow--spin"></div>
                            <p>
                                {{
                                    $l10n.n__(
                                        '%d document en cours de traitement',
                                        '%d documents en cours de traitement',
                                        totalFiles,
                                        totalFiles,
                                    )
                                }}
                            </p>
                            <CompassButton
                                :label="$t.__('ocr.UploadForm.import_other_documents')"
                                type="secondary"
                                class="mt-4"
                            />
                        </template>
                        <template v-else-if="technicalIssue">
                            <i class="icon-email-statut-fail fs-icon-dragndrop red"></i>
                            <p class="semibold">{{ $l10n.__('Erreur technique') }}</p>
                            <p class="wp-6">
                                {{
                                    $l10n.__(
                                        'Notre partenaire rencontre actuellement un problème technique, le service est indisponible pour le moment.',
                                    )
                                }}
                            </p>
                        </template>
                        <template v-else-if="limitExceed">
                            <i class="icon-email-statut-fail fs-icon-dragndrop red"></i>
                            <p class="semibold">{{ $l10n.__('Limite atteinte') }}</p>
                            <p class="wp-6 gray-60">
                                {{ $l10n.__('Vous ne pouvez envoyez que 50 fichiers à la fois.') }}
                            </p>
                        </template>
                        <template v-else-if="quotaExceed">
                            <i class="icon-notification-fillout fs-icon-dragndrop red"></i>
                            <p class="semibold">{{ $l10n.__('Erreur de quota') }}</p>
                            <p class="wp-6 gray-60">
                                {{
                                    $l10n.__(
                                        "Vous avez atteint la totalité de votre quota d'import.",
                                    )
                                }}
                            </p>
                        </template>
                        <template v-else-if="errorFileList.length">
                            <i class="icon-notification-fillout fs-icon-dragndrop red"></i>
                            <p v-if="errorCode === 400" class="semibold fs-3">
                                {{
                                    $l10n.n__(
                                        'Fichier invalide. Vérifiez le poids et le format',
                                        'Fichiers invalides. Vérifiez le poids et le format',
                                        errorFileList.length,
                                        errorFileList.length,
                                    )
                                }}
                            </p>
                            <p v-else class="semibold fs-3">
                                {{
                                    $l10n.n__(
                                        '%d document en erreur',
                                        '%d documents en erreur',
                                        errorFileList.length,
                                        errorFileList.length,
                                    )
                                }}
                            </p>
                            <ul class="m-0">
                                <li
                                    v-for="file of errorsPreviewList"
                                    :key="file.id"
                                    class="gray-60"
                                >
                                    <template v-if="file.file_name">
                                        {{ file.file_name }}
                                    </template>
                                    <template v-else>
                                        {{ file.name }}
                                    </template>
                                </li>
                            </ul>
                            <span v-if="errorFileList.length > 5" class="grey mt-1"
                                >+
                                {{
                                    $l10n.n__(
                                        '%d autre document',
                                        '%d autres documents',
                                        errorFileList.length - 5,
                                        errorFileList.length - 5,
                                    )
                                }}</span
                            >
                        </template>
                        <template v-else>
                            <p class="black el-upload__text m-0 mb-2">
                                {{
                                    $l10n.__(
                                        'Glisser - déposer des documents depuis votre ordinateur',
                                    )
                                }}
                            </p>
                            <p class="grey m-0 mb-2">
                                {{
                                    $l10n.__(
                                        'JPEG, PNG, WEBP image, PDF de maximum 5 pages et inférieur à 5MB.',
                                    )
                                }}
                            </p>
                            <p class="grey m-0">
                                {{ $l10n.__('(Import maximum de 50 fichiers à la fois)') }}
                            </p>
                            <p v-if="isTrial" class="red semibold mb-0">
                                {{
                                    $l10n.__(
                                        'En période d’essai, vous êtes limité à un import de 20 documents',
                                    )
                                }}
                            </p>
                            <CompassButton
                                :label="$t.__('ocr.UploadForm.import_document')"
                                type="secondary"
                                class="mt-3"
                            />
                        </template>
                        <template
                            v-if="
                                (limitExceed || quotaExceed || errorFileList.length) &&
                                !isUploading &&
                                !isProcessing
                            "
                        >
                            <CompassButton
                                :label="$t.__('ocr.UploadForm.back_to_document_import')"
                                type="secondary"
                                class="mt-3"
                                @click="backToUpload"
                            />
                        </template>
                    </div>
                </el-upload>
            </el-form-item>
        </el-form>
    </div>
</template>
<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import LocalStorage from 'Core/commons/LocalStorage';
import { goToUrl } from 'js/vue/helpers';
import { createNamespacedHelpers } from 'vuex';
import Feature from 'Services/Feature';
import Ocr from 'api/ocr';
const corpMapState = createNamespacedHelpers('corp').mapState;

export default {
    props: {
        linkedtype: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isUploading: false,
            fileList: [],
            errorCode: null,
            errorFileList: [],
            remoteFileList: [],
            isProcessing: false,
            totalFiles: null,
            limitExceed: false,
            quotaExceed: false,
        };
    },
    computed: {
        ...corpMapState({
            accountLevel: 'accountLevel',
        }),
        isTrial() {
            return this.accountLevel === 'trial';
        },
        errorsPreviewList() {
            return this.errorFileList.slice(0, 5);
        },
        technicalIssue() {
            return !Feature.isEnabled('ocr-purInvoice');
        },
        actionUploadUrl() {
            return this.$router.resolve({
                name: 'legacy.ocr.create',
                params: { linkedtype: this.linkedtype },
            }).href;
        },
        mimeTypes() {
            return 'image/jpeg, image/png, image/webp, application/pdf';
        },
    },
    created() {
        this.getProcessingList();
        EventBus.on('ocr:failed', (payload) => {
            const data = payload;
            if (!_.isNil(data.reason) && data.reason === 'quota') {
                this.quotaExceed = true;
            }
            if (!_.isNil(data.ocr_in_failure)) {
                const error = new Error();
                error.status = 400;
                this.handleError(error, data.ocr_in_failure);
            }
            this.handleMercureResponse({ data });
        });
        EventBus.on(
            'ocr:completed',
            _.debounce((payload) => {
                const data = payload;
                this.handleMercureResponse({ data });
            }, 1000),
        );
    },
    unmounted() {
        EventBus.off('ocr:completed');
        EventBus.off('ocr:failed');
    },
    methods: {
        handleMercureResponse(payload) {
            if (_.isNil(this.totalFiles) || payload.data.nb_elt_processing < this.totalFiles) {
                this.updateTotalFiles(true, payload.data.nb_elt_processing);
            }
            this.fileList = this.fileList.filter(
                (file) => !['success', 'error'].includes(file.status),
            );

            if (payload.data.nb_elt_processing === 0) {
                this.isProcessing = false;
            }
        },
        handleExceed() {
            this.limitExceed = true;
        },
        handleBeforeUpload(file) {
            if (
                this.checkSize(file.size) &&
                this.checkMimeType(file.type) &&
                !this.technicalIssue
            ) {
                this.fileList.push(file);
                this.isUploading = true;
                this.updateTotalFiles();
                return true;
            }

            const error = new Error();
            error.status = 400;
            this.handleError(error, file);
            return false;
        },
        handleError(err, file, fileList) {
            if (!this.fileList.length || (!_.isNil(fileList) && !fileList.length)) {
                this.isUploading = false;
                this.isProcessing = false;
            }
            this.errorCode = JSON.parse(JSON.stringify(err, null, 2)).status || null;
            this.errorFileList.push(file);
        },
        handleSuccess(response, file, fileList) {
            if (fileList.every((file) => file.status === 'success')) {
                this.isProcessing = true;
                this.isUploading = false;
            }
        },
        handleClickUploader(e) {
            if (!this.isUploading && !this.isProcessing && this.errorFileList.length) {
                e.stopPropagation();
            }
        },
        backToUpload(e) {
            e.stopPropagation();
            this.errorFileList = [];
            this.fileList = [];
            this.updateTotalFiles(true, 0);
            this.limitExceed = false;
            this.quotaExceed = false;
        },
        submitUpload() {
            this.$refs.uploader.submit();
        },
        checkSize(size) {
            return size <= 5242880;
        },
        checkMimeType(type) {
            return this.mimeTypes.replace(/\s/g, '').split(',').includes(type);
        },
        updateTotalFiles(force, length) {
            this.totalFiles = force ? length : this.remoteFileList.length + this.fileList.length;
        },
        /**
         * get current processing files list
         */
        getProcessingList() {
            Ocr.searchOcrInvoice({ filters: { state: ['pending', 'processing'] } }).then(
                (response) => {
                    if (response.data.data.length) {
                        this.remoteFileList = response.data.data;
                        this.updateTotalFiles();
                        this.isProcessing = true;
                    }
                },
            );
        },
    },
    components: {
        Preloader,
    },
};
</script>

<style lang="scss">
.upload-area {
    .undrag {
        .el-upload,
        .el-upload-dragger {
            cursor: default;
        }

        .el-upload-dragger {
            border: none;
        }
    }

    .el-upload,
    .el-upload-dragger {
        width: 100%;
        max-height: 250;
        height: 100%;
    }

    .fs-icon-dragndrop {
        font-size: 32px;
    }

    .loader {
        position: relative;
        box-sizing: content-box;
        color: black;
        font-size: 12px;
        width: 1em;
        height: 1em;

        &-arrow {
            &--spin {
                animation: spin 1s linear infinite;
                border: 0.2em solid transparent;
                border-top-color: currentcolor;
                border-bottom-color: currentcolor;
                border-radius: 50%;

                &:before,
                &:after {
                    content: '';
                    font-size: 1em;
                    position: absolute;
                    border: 0.3em solid transparent;
                    transform: rotate(-45deg);
                }

                &:before {
                    top: -(0.3em * 0.25);
                    right: -(0.3em * 1.25);
                    border-top-color: currentcolor;
                }

                &:after {
                    bottom: -(0.3em * 0.25);
                    left: -(0.3em * 1.25);
                    border-bottom-color: currentcolor;
                }
            }
        }
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
